import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CreditCard, Loader2, History, ShieldAlert, ShieldCheck, CalendarPlus, RotateCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  ACTIVE: { label: "Active", cls: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" },
  PAST_DUE: { label: "Past due", cls: "bg-red-500/15 text-red-400 border border-red-500/30" },
  GRACE_PERIOD: { label: "Grace period", cls: "bg-amber-500/15 text-amber-400 border border-amber-500/30" },
  PENDING_PAYMENT_METHOD: { label: "Pending card", cls: "bg-sky-500/15 text-sky-300 border border-sky-500/30" },
  TERMINATED: { label: "Terminated", cls: "bg-muted text-muted-foreground border border-border" },
};
const FILTERS = ["all", "ACTIVE", "PAST_DUE", "GRACE_PERIOD", "PENDING_PAYMENT_METHOD"];

export default function ZoneBillingPanel() {
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [extendDays, setExtendDays] = useState(30);
  const [history, setHistory] = useState(null);
  const [historyZone, setHistoryZone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/zone-billing", { params: filter === "all" ? {} : { status: filter } });
      setData(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (zoneId, path, body, msg) => {
    try { await api.post(`/admin/leases/${zoneId}/${path}`, body || {}); toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openHistory = async (z) => {
    setHistoryZone(z); setHistory(null);
    try { const { data } = await api.get(`/admin/leases/${z.zoneId}/history`); setHistory(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setHistory([]); }
  };

  const counts = data.counts || {};

  return (
    <div className="space-y-5" data-testid="zone-billing-panel">
      <div>
        <h2 className="font-heading text-lg font-bold">Zone billing</h2>
        <p className="text-sm text-muted-foreground">Lease revenue, renewal dates, and dunning across every leased zone.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="zone-billing-filters">
        {FILTERS.map((f) => (
          <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)} data-testid={`zone-billing-filter-${f}`}>
            {f === "all" ? "All" : (STATUS[f]?.label || f)}
            {f !== "all" && counts[f] != null && <span className="ml-1.5 opacity-70">{counts[f]}</span>}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : data.items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="zone-billing-empty">
          <CreditCard className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <div className="font-heading font-bold">No leases here</div>
          <p className="text-sm text-muted-foreground mt-1">No zone leases match this filter.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zone</TableHead>
                <TableHead>Current lessee</TableHead>
                <TableHead>Lease amount</TableHead>
                <TableHead>Renewal</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Grace override</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((z) => {
                const s = STATUS[z.billingStatus] || STATUS.ACTIVE;
                const isLate = z.billingStatus === "PAST_DUE" || z.billingStatus === "GRACE_PERIOD";
                return (
                  <TableRow key={z.zoneId} data-testid={`zone-billing-row-${z.zoneId}`}>
                    <TableCell>
                      <div className="font-medium">{z.zoneName || "Zone"}</div>
                      <div className="text-xs text-muted-foreground">{z.humanId || z.zoneId?.slice(-6)}</div>
                    </TableCell>
                    <TableCell>
                      {z.lesseeName || <span className="text-muted-foreground">—</span>}
                      {z.lesseeEmail && <div className="text-xs text-muted-foreground" data-testid={`zone-billing-lessee-email-${z.zoneId}`}>{z.lesseeEmail}</div>}
                    </TableCell>
                    <TableCell>${Number(z.annualFee).toFixed(2)}/yr</TableCell>
                    <TableCell>
                      {z.renewalDate || <span className="text-muted-foreground">—</span>}
                      {z.graceUntil && <div className="text-xs text-amber-400">grace → {z.graceUntil}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge className={s.cls} data-testid={`zone-billing-status-${z.zoneId}`}>{s.label}</Badge>
                        {z.autoTerminateSuspended && <Badge className="bg-purple-500/15 text-purple-300 border border-purple-500/30" title="Auto-termination suspended"><ShieldAlert className="w-3 h-3" /></Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {isLate && (
                          <>
                            <Button size="sm" variant="outline" title="Extend grace" onClick={() => act(z.zoneId, "extend-grace", { days: Number(extendDays) }, `Grace extended ${extendDays} days`)} data-testid={`grace-extend-${z.zoneId}`}><CalendarPlus className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline" title="Reset dunning clock" onClick={() => act(z.zoneId, "reset-grace", {}, "Grace period reset")} data-testid={`grace-reset-${z.zoneId}`}><RotateCcw className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline" title="Mark payment resolved" onClick={() => act(z.zoneId, "mark-resolved", {}, "Marked as resolved")} data-testid={`grace-resolve-${z.zoneId}`}><CheckCircle2 className="w-4 h-4" /></Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" title={z.autoTerminateSuspended ? "Resume auto-termination" : "Suspend auto-termination"}
                          onClick={() => act(z.zoneId, "suspend-termination", { suspended: !z.autoTerminateSuspended }, z.autoTerminateSuspended ? "Auto-termination resumed" : "Auto-termination suspended")}
                          data-testid={`grace-suspend-${z.zoneId}`}>
                          {z.autoTerminateSuspended ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
                        </Button>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="ghost" title="Lease history" onClick={() => openHistory(z)} data-testid={`lease-history-${z.zoneId}`}><History className="w-4 h-4" /></Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader><DialogTitle>Lease history — {historyZone?.zoneName}</DialogTitle>
                              <DialogDescription>Every billing and lease event recorded for this zone.</DialogDescription>
                            </DialogHeader>
                            {history == null ? (
                              <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
                            ) : history.length === 0 ? (
                              <p className="text-sm text-muted-foreground py-6 text-center">No events recorded.</p>
                            ) : (
                              <div className="max-h-80 overflow-y-auto divide-y divide-border" data-testid="lease-history-list">
                                {history.map((e, i) => (
                                  <div key={i} className="py-2.5 flex items-start justify-between gap-3">
                                    <div>
                                      <div className="font-medium text-sm">{e.event}</div>
                                      <div className="text-xs text-muted-foreground">{e.shopName || "—"} · by {e.actor}</div>
                                    </div>
                                    <div className="text-xs text-muted-foreground whitespace-nowrap">{(e.timestamp || "").slice(0, 10)}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Label htmlFor="grace-days" className="text-xs">Extend-grace length (days)</Label>
        <Input id="grace-days" type="number" min={1} value={extendDays} onChange={(e) => setExtendDays(e.target.value)} className="w-24 h-8" data-testid="grace-days-input" />
      </div>
    </div>
  );
}
